/**
 * AllergyChipInput — tag-style chip input for pet allergies.
 *
 * Accepts/emits an array of strings. The user types in the input and presses
 * Enter or comma to add a chip. Chips can be removed with the × button or
 * Backspace when the input is empty.
 *
 * The backing store serialises to/from JSON arrays (the `allergiesJson` column
 * on pets). Use `parseAllergiesJson` / `serializeAllergiesJson` helpers for the
 * API boundary.
 */
import { X } from 'lucide-react';
import { useRef, useState } from 'react';

export function parseAllergiesJson(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
    }
  } catch {
    // If it's not valid JSON, treat it as a comma-separated string.
    if (typeof raw === 'string' && raw.trim()) {
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

export function serializeAllergiesJson(tags: string[]): string | null {
  if (tags.length === 0) return null;
  return JSON.stringify(tags);
}

interface AllergyChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  id?: string;
}

export function AllergyChipInput({ value, onChange, id }: AllergyChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function addTag(tag: string) {
    const trimmed = tag.trim();
    if (!trimmed || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInputValue('');
  }

  function removeTag(tag: string) {
    onChange(value.filter((t) => t !== tag));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function handleBlur() {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  }

  return (
    <div
      className="flex min-h-[36px] flex-wrap gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring/30 cursor-text"
      onClick={() => inputRef.current?.focus()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.focus();
      }}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium"
        >
          {tag}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeTag(tag);
            }}
            aria-label={`Remove ${tag}`}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-3" strokeWidth={2} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={value.length === 0 ? 'Type and press Enter to add…' : ''}
        className="min-w-[120px] max-w-full flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
      />
    </div>
  );
}
